import type { IMessage, IChatStoreState } from '@/modules/chat-matrix-fast/types/IChat'
import {
  chatOpenJumpLog,
  summarizeJumpMessage,
} from '@/modules/chat-matrix-fast/utils/chatOpenJumpLog'
import { resolveActiveMessageRoomId } from '@/modules/chat-matrix-fast/utils/topics'

const ROOM_MESSAGES_CACHE_LIMIT = 15

function trimRoomId(roomId: string | null | undefined): string {
  return roomId?.trim() || ''
}

function cloneMessages(messages: IMessage[]): IMessage[] {
  return messages.map(message => ({
    ...message,
    attachments: [...message.attachments],
    reactions: message.reactions ? [...message.reactions] : message.reactions,
    message_reactions: message.message_reactions
      ? [...message.message_reactions]
      : message.message_reactions,
    mention_user_ids: message.mention_user_ids
      ? [...message.mention_user_ids]
      : message.mention_user_ids,
  }))
}

function trimCache(cache: Map<string, IMessage[]>): void {
  while (cache.size > ROOM_MESSAGES_CACHE_LIMIT) {
    const oldestKey = cache.keys().next().value
    if (!oldestKey) {
      return
    }
    cache.delete(oldestKey)
  }
}

export function cacheRoomMessages(
  state: IChatStoreState,
  roomId: string | null | undefined,
  messages: IMessage[],
): void {
  const normalizedRoomId = trimRoomId(roomId)
  if (!normalizedRoomId || messages.length === 0) {
    return
  }

  state.messagesByRoomId.delete(normalizedRoomId)
  state.messagesByRoomId.set(normalizedRoomId, cloneMessages(messages))
  trimCache(state.messagesByRoomId)
}

export function cacheActiveMessages(state: IChatStoreState): void {
  const roomId = resolveActiveMessageRoomId(state)
  if (!roomId) {
    return
  }

  cacheRoomMessages(state, roomId, state.messages)
}

export function hasRoomMessagesCache(
  state: Pick<IChatStoreState, 'messagesByRoomId'>,
  roomId: string | null | undefined,
): boolean {
  const normalizedRoomId = trimRoomId(roomId)
  return Boolean(normalizedRoomId && state.messagesByRoomId.get(normalizedRoomId)?.length)
}

export function restoreRoomMessages(
  state: IChatStoreState,
  roomId: string | null | undefined,
): boolean {
  const normalizedRoomId = trimRoomId(roomId)
  if (!normalizedRoomId) {
    return false
  }

  const snapshot = state.messagesByRoomId.get(normalizedRoomId)
  if (!snapshot?.length) {
    return false
  }

  state.messagesByRoomId.delete(normalizedRoomId)
  state.messagesByRoomId.set(normalizedRoomId, snapshot)
  state.messages.length = 0
  state.messages.push(...cloneMessages(snapshot))
  return true
}

function messageTimelineIdentity(message: IMessage): string {
  if (message.matrix_event_id) {
    return `e:${message.matrix_event_id}`
  }
  if (message.matrix_txn_id) {
    return `t:${message.matrix_txn_id}`
  }
  return `i:${String(message.id)}`
}

function sameTimelineIdentity(existing: IMessage[], next: IMessage[]): boolean {
  if (existing.length !== next.length) {
    return false
  }
  for (let index = 0; index < existing.length; index += 1) {
    if (messageTimelineIdentity(existing[index]) !== messageTimelineIdentity(next[index])) {
      return false
    }
  }
  return true
}

function sharedTailOffset(existing: IMessage[], next: IMessage[]): number | null {
  if (existing.length === 0 || next.length <= existing.length) {
    return null
  }

  const offset = next.length - existing.length
  for (let index = 0; index < existing.length; index += 1) {
    if (
      messageTimelineIdentity(existing[index])
      !== messageTimelineIdentity(next[offset + index])
    ) {
      return null
    }
  }
  return offset
}

export function replaceActiveMessages(
  state: IChatStoreState,
  messages: IMessage[],
  roomId?: string | null,
): void {
  const cacheRoomId = roomId ?? resolveActiveMessageRoomId(state)
  const beforeCount = state.messages.length
  const beforeFirst = summarizeJumpMessage(state.messages[0])
  const beforeLast = summarizeJumpMessage(state.messages[state.messages.length - 1])
  const afterFirst = summarizeJumpMessage(messages[0])
  const afterLast = summarizeJumpMessage(messages[messages.length - 1])

  if (sameTimelineIdentity(state.messages, messages)) {
    for (let index = 0; index < messages.length; index += 1) {
      state.messages[index] = messages[index]
    }
    cacheRoomMessages(state, cacheRoomId, messages)
    chatOpenJumpLog('replaceActiveMessages · patch', {
      roomId: cacheRoomId,
      path: 'patch',
      beforeCount,
      afterCount: messages.length,
      beforeFirst,
      beforeLast,
      afterFirst,
      afterLast,
    })
    return
  }

  const prependCount = sharedTailOffset(state.messages, messages)
  if (prependCount != null) {
    state.messages.unshift(...messages.slice(0, prependCount))
    for (let index = prependCount; index < messages.length; index += 1) {
      state.messages[index] = messages[index]
    }
    cacheRoomMessages(state, cacheRoomId, messages)
    chatOpenJumpLog('replaceActiveMessages · prepend', {
      roomId: cacheRoomId,
      path: 'prepend',
      prependCount,
      beforeCount,
      afterCount: messages.length,
      beforeFirst,
      beforeLast,
      afterFirst,
      afterLast,
    })
    return
  }

  state.messages.length = 0
  state.messages.push(...messages)
  cacheRoomMessages(state, cacheRoomId, messages)
  chatOpenJumpLog('replaceActiveMessages · FULL REPLACE', {
    roomId: cacheRoomId,
    path: 'replace',
    beforeCount,
    afterCount: messages.length,
    beforeFirst,
    beforeLast,
    afterFirst,
    afterLast,
  })
}

export function clearMessages(
  state: IChatStoreState,
  options?: { preserveActiveRoom?: boolean; preserveRoomId?: string | null },
): void {
  if (options?.preserveRoomId) {
    cacheRoomMessages(state, options.preserveRoomId, state.messages)
  } else if (options?.preserveActiveRoom) {
    cacheActiveMessages(state)
  }
  state.messages.length = 0
}

export function dropRoomMessages(state: IChatStoreState, roomId: string | null | undefined): void {
  const normalizedRoomId = trimRoomId(roomId)
  if (!normalizedRoomId) {
    return
  }
  state.messagesByRoomId.delete(normalizedRoomId)
}

export function clearAllRoomMessages(state: IChatStoreState): void {
  state.messagesByRoomId.clear()
}
